import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'
import { useReactFlow, NodeProps } from '@xyflow/react'
import {
  Filter,
  Settings,
  HelpCircle,
  RefreshCw,
  Clipboard,
  Expand,
  Minimize,
  Shield,
} from 'lucide-react'
import React, { memo, useRef, useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import useDataSettings from '@/hooks/useDataSettings'
import '~/styles/introjs.css'
import useDocumentState from '@/hooks/useDocumentState'
import useIntroTour from '@/hooks/useIntroTour'
import useSectionManagement from '@/hooks/useSectionManagement'
import DocumentStyleSelector, { DocumentStyle } from '../../document/DocumentStyleSelector'
import DocumentTabs from '../../document/DocumentTabs'
import NodeConfigurationDialog from '../../dialogs/configuration-dialog/NodeConfigurationDialog'
import NodeReferenceSelectorDialog from '../../dialogs/reference-selector-dialog/NodeReferenceSelectorDialog'

// Remove the SectionContextMenu import as we'll use the global one

interface nodeknowledgesetData {
  id: string
  position: { x: number; y: number }
  data: Record<string, unknown>
  label?: string
  contentMarkdown?: string
  linkedKnowledge?: string
  assignedEmails?: string[]
  isLocked?: boolean
  isMinimized?: boolean
  configuration?: any
  dataSettings?: any
  documentStyle?: DocumentStyle
  protectedSections?: Record<string, boolean>
  processingStates?: Record<string, boolean>
  sectionExplanations?: Record<string, string>
  sectionExplanationStates?: Record<string, boolean>
  sectionCommand?: {
    command: 'Draft' | 'Detailed' | 'Styled'
    sectionId: string
    sectionTitle: string
    content: string
    processing?: boolean
    'sysagent-cmd'?: string
    'sysagent-response'?: string
    for_review?: string
  }
  context?: string
  setReferences?: Record<string, string[]>
}

const defaultConfig = {
  modifierPrompt: '',
  enableAutomation: false,
  markdownFormatVisible: true,
}

const NodeKnowledgeSet: React.FC<NodeProps<nodeknowledgesetData>> = ({
  id,
  data,
  selected,
}) => {
  const { updateNodeData } = useReactFlow()
  const nodeBaseContainerRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const markdownPreviewRef = useRef<HTMLDivElement>(null)

  const [minimizeLevel, setMinimizeLevel] = useState(0)

  const {
    contentMarkdown,
    setContentMarkdown,
    statusMessage,
    setStatusMessage,
    assignedEmails,
    setAssignedEmails,
    isLocked,
    setIsLocked,
    isMinimized,
    setIsMinimized,
    isMarkdownExpanded,
    setIsMarkdownExpanded,
    nodeConfig,
    setNodeConfig,
    documentStyle,
    setDocumentStyle,
    context,
    setContext,
    linkedKnowledge,
    setLinkedKnowledge,
    isConfigDialogVisible,
    setIsConfigDialogVisible,
    isDocLibraryDialogVisible,
    setIsDocLibraryDialogVisible,
    isUpdateWithAIDialogVisible,
    setIsUpdateWithAIDialogVisible,
    isDataSettingsDialogVisible,
    setIsDataSettingsDialogVisible,
    showDebugInfo,
    setShowDebugInfo,
    copyFeedback,
    setCopyFeedback,
    syncNodeData,
    handleLock,
    handleMinimize,
    handleUpdateWithAI,
    handleLoadDocumentTemplates,
    handleStyleChange,
    handleContentChange,
    handleContextChange,
    handleLinkedKnowledgeChange,
  } = useDocumentState(id, data, updateNodeData)

  // Add useSectionManagement for section reviews
  const {
    sections,
    selectedSectionId,
    sectionStates,
    sectionReviews,
    setSectionStates,
    handleAcceptReview,
    handleRejectReview,
    handleSectionSelect,
    // ...other section management hooks if needed
  } = useSectionManagement(
    id,
    data,
    syncNodeData,
    contentMarkdown,
    setContentMarkdown
  )

  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
    generateLinkedKnowledgeText,
  } = useDataSettings(
    id,
    data,
    syncNodeData,
    false,
    context,
    sections,
    contentMarkdown,
    setIsDataSettingsDialogVisible
  )

  const [isReferenceDialogVisible, setIsReferenceDialogVisible] =
    useState(false)
  const [expanded, setExpanded] = useState(false)

  // Add selectedSectionId and selectedSectionName to track the current section
  const [selectedSectionName, setSelectedSectionName] = useState<string | null>(
    null
  )

  // Section states and protection
  const [protectedSections, setProtectedSections] = useState<Record<string, boolean>>(data.protectedSections || {})

  // Remove local section menu state variables since we'll use the global one

  useEffect(() => {
    if (data.setReferences) {
      // Load saved references
      handleSaveReferences(data.setReferences, true)
    }
  }, [])

  // Sync local state when data.contentMarkdown, data.linkedKnowledge, data.context, or data.dataSettings changes externally
  useEffect(() => {
    // Only update if the new value is different from local state
    if (
      data.contentMarkdown !== undefined &&
      data.contentMarkdown !== contentMarkdown
    ) {
      setContentMarkdown(data.contentMarkdown)
    }
    if (
      data.linkedKnowledge !== undefined &&
      data.linkedKnowledge !== linkedKnowledge
    ) {
      setLinkedKnowledge(data.linkedKnowledge)
    }
    if (data.context !== undefined && data.context !== sectionStates.context) {
      setSectionStates((prev) => ({ ...prev, context: data.context }))
    }
    // Update documentStyle if changed externally
    if (
      data.documentStyle !== undefined &&
      JSON.stringify(data.documentStyle) !== JSON.stringify(documentStyle)
    ) {
      setDocumentStyle(data.documentStyle)
    }
  }, [
    data.contentMarkdown,
    data.linkedKnowledge,
    data.context,
    data.documentStyle, // add this dependency
  ])

  const { introEnabled, setIntroEnabled, introSteps, introOptions } =
    useIntroTour(id, null)

  // Initialize minimize level from data
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' })
    } else {
      setMinimizeLevel(data.minimizeLevel)
      setIsMinimized(data.minimizeLevel !== 'normal')
    }
  }, [data.minimizeLevel, id, data, updateNodeData])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}

    if (data.knowledgeItems && data.knowledgeItems.length > 0) {
      minifiedInfo.items = `${data.knowledgeItems.length} item${data.knowledgeItems.length !== 1 ? 's' : ''}`
    }

    if (data.tags && data.tags.length > 0) {
      minifiedInfo.tags = `${data.tags.length} tag${data.tags.length !== 1 ? 's' : ''}`
    }

    if (data.category) {
      minifiedInfo.category = data.category
    }

    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges =
      JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)

    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo })
    }
  }, [
    data.knowledgeItems,
    data.tags,
    data.category,
    data.minifiedInfo,
    id,
    data,
    updateNodeData,
  ])

  const handleAssignUsersClick = () => {
    const emailsStr = window.prompt(
      'Enter emails separated by space',
      assignedEmails.join(' ')
    )
    if (emailsStr !== null) {
      const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
      setAssignedEmails(emailsArr)
      syncNodeData({ assignedEmails: emailsArr })
    }
  }

  const handleTitleChange = (title: string) => {
    syncNodeData({ label: title })
  }

  const handleRefreshLinkedKnowledge = () => {
    const generated = generateLinkedKnowledgeText(dataSettings)
    syncNodeData({ linkedKnowledge: generated })
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(contentMarkdown)
  }

  const handleExpandToggle = () => {
    setExpanded(!expanded)
  }

  // Handler for toggling explanation minimize/maximize state
  const handleToggleExplanation = (sectionId: string) => {
    const currentExplanationStates =
      (data.sectionExplanationStates as Record<string, boolean>) || {}
    const newState = !currentExplanationStates[sectionId] // Toggle state (undefined or false becomes true, true becomes false)

    syncNodeData({
      sectionExplanationStates: {
        ...currentExplanationStates,
        [sectionId]: newState,
      },
    })
  }

  const handleSaveReferences = (
    selected: Record<string, string[]>,
    isLoading = false
  ) => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []

    // Extract all existing sections
    const existingSections: Record<string, string> = {}
    const sectionRegex = /::: section ([^\n]+)\n([\s\S]*?)\n:::/g
    let match
    while ((match = sectionRegex.exec(contentMarkdown)) !== null) {
      const sid = match[1].trim()
      const content = match[2]
      existingSections[sid] = content
    }

    console.log('Existing sections:', existingSections)
    console.log('Protected sections:', protectedSections)

    let combined = ''

    Object.entries(selected).forEach(([nodeId, dataPointIds]) => {
      const node = allNodes.find((n: any) => n.id === nodeId)
      if (!node) return

      dataPointIds.forEach((dpId) => {
        let content = ''
        let sectionHeader = dpId

        if (dpId === 'fullDocument') {
          content = node.data?.contentMarkdown || ''
          sectionHeader = 'fullDocument'
        } else if (dpId.startsWith('section:')) {
          const sectionName = dpId.replace('section:', '')
          content =
            node.data?.dataSettings?.documentSections?.sections?.[sectionName]
              ?.value || ''
          sectionHeader = `section:${sectionName}`
        } else if (dpId.startsWith('exposed:')) {
          const key = dpId.replace('exposed:', '')
          content = node.data?.dataSettings?.exposedData?.[key]?.value || ''
          sectionHeader = `exposed:${key}`
        }

        // Check for protection by any associated identifier
        const isProtected = Object.entries(protectedSections).some(
          ([key, isProtected]) => {
            // Check if this section matches any protected pattern
            return (
              isProtected &&
              (key === sectionHeader ||
                key === selectedSectionId ||
                key === selectedSectionName)
            )
          }
        )

        console.log(`Section ${sectionHeader} protection status:`, isProtected)

        // Use existing content if protected and exists
        if (isProtected && existingSections[sectionHeader] !== undefined) {
          combined += `\n::: section ${sectionHeader}\n${existingSections[sectionHeader]}\n:::\n`
        } else {
          combined += `\n::: section ${sectionHeader}\n${content}\n:::\n`
        }
      })
    })

    syncNodeData({
      contentMarkdown: combined,
      ...(isLoading ? {} : { setReferences: selected }),
    })
  }

  // This handler will be called by the global section menu via updateNodeData
  useEffect(() => {
    // Set up a listener for section protection changes from the global context menu
    const handleSectionProtectionChange = (event: CustomEvent) => {
      if (event.detail.nodeId === id && event.detail.sectionId) {
        const { sectionId, sectionName, isProtected } = event.detail

        // Update protected sections
        const updatedProtectedSections = {
          ...protectedSections,
          [sectionId]: isProtected,
          [sectionName]: isProtected,
        }

        setProtectedSections(updatedProtectedSections)
        syncNodeData({ protectedSections: updatedProtectedSections })
      }
    }

    document.addEventListener(
      'section-protection-change',
      handleSectionProtectionChange as EventListener
    )
    return () => {
      document.removeEventListener(
        'section-protection-change',
        handleSectionProtectionChange as EventListener
      )
    }
  }, [id, protectedSections, syncNodeData])

  // Update to use the handleSectionSelect with section name and show context menu
  const customHandleSectionSelect = (
    sectionId: string,
    sectionName: string,
    event: React.MouseEvent
  ) => {
    handleSectionSelect(sectionId, sectionName, event)

    // Use the existing flow section select mechanism
    const flowSectionSelect = (window as any).__flowSectionSelect
    if (flowSectionSelect) {
      flowSectionSelect(id, sectionId, sectionName, {
        clientX: event.clientX,
        clientY: event.clientY,
        pageX: event.pageX,
        pageY: event.pageY,
        screenX: event.screenX,
        screenY: event.screenY,
        type: event.type,
      })
    }
  }

  // Handle section state change from the global menu system
  useEffect(() => {
    const handleGlobalSectionCommand = (event: CustomEvent) => {
      const { nodeId, sectionId, command } = event.detail

      if (nodeId !== id) return // Only handle commands for this node

      const section = sections.get(sectionId)
      if (!section) return

      console.log(
        `KnowledgeSet - Setting section ${sectionId} to processing state for command: ${command}`
      )
      console.log(
        'KnowledgeSet - Current data.processingStates:',
        data.processingStates
      )

      // First, set the section as processing
      const updatedProcessingStates = {
        ...(data.processingStates || {}),
        [sectionId]: true,
      }

      console.log(
        'KnowledgeSet - Updated processing states:',
        updatedProcessingStates
      )

      // Update both processing states and section command
      syncNodeData({
        processingStates: updatedProcessingStates,
        sectionCommand: {
          command: command,
          sectionId: sectionId,
          sectionTitle: section.title,
          content: section.content,
          processing: true,
        },
      })
    }

    // Listen for section commands from the global menu system
    window.addEventListener(
      'flowSectionCommand',
      handleGlobalSectionCommand as EventListener
    )

    return () => {
      window.removeEventListener(
        'flowSectionCommand',
        handleGlobalSectionCommand as EventListener
      )
    }
  }, [id, sections, data.processingStates, syncNodeData])

  // Monitor section command processing and clear processing state when done
  useEffect(() => {
    // Only clear processing state when sectionCommand is explicitly null or processing is false
    if (
      data.sectionCommand?.sectionId &&
      data.sectionCommand.processing === false
    ) {
      console.log(
        `KnowledgeSet - Clearing processing state for section: ${data.sectionCommand.sectionId}`
      )
      const updatedProcessingStates = {
        ...(data.processingStates || {}),
        [data.sectionCommand.sectionId]: false,
      }
      syncNodeData({
        processingStates: updatedProcessingStates,
        sectionCommand: null,
      })
    }
  }, [data.sectionCommand, data.processingStates, syncNodeData])

  // Debug log to see current processing states
  useEffect(() => {
    console.log(
      'KnowledgeSet - Current data.processingStates:',
      data.processingStates
    )
    console.log(
      'KnowledgeSet - Current data.sectionCommand:',
      data.sectionCommand
    )
  }, [data.processingStates, data.sectionCommand])

  const nodeStatus = useMemo(
    () => {
      // Add your logic here based on your node's AI/processing state
      return 'initial'
    },
    [
      /* add your dependencies here */
    ]
  )

  const renderConfigContent = (config, setConfig) => (
    <div className="flex flex-col gap-3 mt-2">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={config.markdownFormatVisible || false}
          onChange={(e) =>
            setConfig({ ...config, markdownFormatVisible: e.target.checked })
          }
        />
        <label className="text-sm">Show Markdown format tab</label>
      </div>
    </div>
  )

  const customButtons = [
    {
      id: '1',
      onClick: handleAssignUsersClick,
      icon: <HelpCircle size={16} />,
      tooltip: 'Assign user(s)',
    },
    {
      id: '3',
      onClick: handleOpenDataSettingsDialog,
      icon: <Filter size={16} />,
      tooltip: 'Data connection settings',
    },
    {
      id: '4',
      onClick: handleRefreshLinkedKnowledge,
      icon: <RefreshCw size={16} />,
      tooltip: 'Refresh imported data',
    },
    {
      id: '69',
      onClick: () => handleSaveReferences(data.setReferences),
      icon: <Shield size={16} />,
      tooltip: 'Refresh knowledge set',
    },
  ]

  // Build availableNodes dynamically from flow
  const availableNodes = React.useMemo(() => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    return allNodes
      .filter((n: any) => n.id !== id && n.data)
      .map((n: any) => {
        const dataPoints: {
          id: string
          label: string
          description?: string
        }[] = []

        // Add full document if exists
        if (n.data?.dataSettings?.documentSections?.fullDocument) {
          dataPoints.push({ id: 'fullDocument', label: 'Full Document' })
        }

        // Add all sections if exist
        const sections = n.data?.dataSettings?.documentSections?.sections || {}
        Object.entries(sections).forEach(([title, section]: [string, any]) => {
          dataPoints.push({ id: `section:${title}`, label: title })
        })

        // Add all exposed data points if exist
        const exposedData = n.data?.dataSettings?.exposedData || {}
        Object.entries(exposedData).forEach(([key, field]: [string, any]) => {
          dataPoints.push({
            id: `exposed:${key}`,
            label: field.label || key,
            description: field.description || '',
          })
        })

        return {
          id: n.id,
          label: n.data?.label || n.id,
          dataPoints,
        }
      })
  }, [id])

  // Compute status for NodeBaseContainer

  const handleMinimizeLevelChange = (
    level: 'normal' | 'medium' | 'minimum'
  ) => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, {
      ...data,
      minimizeLevel: level,
      isMinimized: level !== 'normal',
    })
  }

  return (
    <>
      <NodeBaseContainer
        id={id}
        data={data}
        nodeTitle={data.label || 'Knowledge Set'}
        selected={selected} // ✅ CRITICAL: Make sure selected prop is passed!
        isLocked={isLocked}
        resizable={true}
        isMinimized={isMinimized}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onTitleChange={handleTitleChange}
        assignedEmails={assignedEmails}
        onAssignUsersClick={handleAssignUsersClick}
        onConfigClick={() => setIsConfigDialogVisible(true)}
        customButtons={customButtons}
        ref={nodeBaseContainerRef}
        status={nodeStatus}
        minimizeLevel={data.minimizeLevel || 'normal'}
        onMinimizeLevelChange={handleMinimizeLevelChange}
      >
        {(data.minimizeLevel || 'normal') === 'normal' && (
          <div className="flex flex-col h-full">
            <DocumentTabs
              contentMarkdown={contentMarkdown}
              onContentChange={(val) => {
                setContentMarkdown(val)
                syncNodeData({ contentMarkdown: val })
              }}
              nodeId={id}
              context={sectionStates.context || ''}
              onContextChange={(val) => {
                setSectionStates((prev) => ({ ...prev, context: val }))
                syncNodeData({ context: val })
              }}
              linkedKnowledge={linkedKnowledge}
              onLinkedKnowledgeChange={(lk) => {
                setLinkedKnowledge(lk)
                syncNodeData({ linkedKnowledge: lk })
              }}
              isMarkdownExpanded={expanded}
              markdownHeight={
                textareaRef.current?.scrollHeight
                  ? `${textareaRef.current.scrollHeight}px`
                  : 'auto'
              }
              textareaHeight={
                textareaRef.current?.scrollHeight
                  ? `${textareaRef.current.scrollHeight}px`
                  : 'auto'
              }
              nodebaseHeight={
                nodeBaseContainerRef.current?.offsetHeight
                  ? `${nodeBaseContainerRef.current.offsetHeight - 100}px`
                  : 'auto'
              }
              markdownFormatVisible={nodeConfig.markdownFormatVisible}
              onSectionSelect={customHandleSectionSelect} // Use custom handler instead of handleSectionSelect
              sectionStates={sectionStates}
              processingStates={data.processingStates || {}} // Add processingStates support
              sectionReviews={sectionReviews}
              onAcceptReview={handleAcceptReview}
              onRejectReview={handleRejectReview}
              markdownPreviewRef={markdownPreviewRef}
              onRefreshLinkedKnowledge={handleRefreshLinkedKnowledge}
              protectedSections={protectedSections}
              sectionExplanations={
                (data.sectionExplanations as Record<string, string>) || {}
              } // Add sectionExplanations prop
              sectionExplanationStates={
                (data.sectionExplanationStates as Record<string, boolean>) ||
                {}
              } // Add explanation states
              onToggleExplanation={handleToggleExplanation} // Add toggle handler
            />
          </div>
        )}
      </NodeBaseContainer>

      {/* Remove the local SectionContextMenu component as we'll use the global one */}

      {isConfigDialogVisible && (
        <NodeConfigurationDialog
          visible={isConfigDialogVisible}
          onHide={() => setIsConfigDialogVisible(false)}
          configuration={nodeConfig}
          onSave={(config) => {
            setNodeConfig(config)
            syncNodeData({ configuration: config })
          }}
          additionalContent={renderConfigContent}
        />
      )}

      {isDataSettingsDialogVisible && (
        <NodeDataSettingsDialog
          visible={isDataSettingsDialogVisible}
          onHide={() => setIsDataSettingsDialogVisible(false)}
          settings={dataSettings}
          nodeId={id}
          onSettingsChange={handleDataSettingsChange}
          onRemoveInputLink={handleRemoveInputLink}
          onSyncInputLink={handleSyncInputLink}
          forceShowAvailableNodes={true}
          debug={false}
        />
      )}

      {isReferenceDialogVisible && (
        <NodeReferenceSelectorDialog
          visible={isReferenceDialogVisible}
          onHide={() => setIsReferenceDialogVisible(false)}
          availableNodes={availableNodes}
          onSave={handleSaveReferences}
          initialSelected={data.setReferences}
        />
      )}
    </>
  )
}

export default memo(NodeKnowledgeSet)
